import React, { useState, type FormEvent } from "react";

import { useSelector } from "react-redux";

import {
  Modal,
  Accordion,
  Icon,
  Form,
  Placeholder,
  PlaceholderHeader,
  PlaceholderLine,
  PlaceholderParagraph,
  type AccordionTitleProps,
  type CheckboxProps
} from "semantic-ui-react";

import { Button } from "kit/ui";
import ModalProvider from "core/ModalProvider";
import { COLORS } from "styles-js";

import useCommunicationTemplate from "@src/hooks/useCommunicationTemplate";
import { useModal } from "../../contexts/ModalContext";

import type { RootState } from "@src/store/configureStore";

import CookieDetail from "./CookieDetail.component";

import * as styles from "./CookieModal.styles.js";


const COOKIE_ITEMS = {
  necessary: [
    {
      name: "CookieConsent",
      provider: "mykredit.es",
      expiration: "1 año",
      purpose: "Almacena el estado de consentimiento de cookies del usuario para el dominio actual.",
      source: "https://mykredit.es/",
      processor: "mykredit.es"
    },
    {
      name: "test_cookie",
      provider: "mykredit.es",
      expiration: "1 día",
      purpose: "Utilizada para comprobar si el navegador del usuario admite cookies.",
      source: "https://policies.google.com/privacy",
      processor: "Google"
    },
    {
      name: "app:clientUnique",
      provider: "mykredit.es",
      expiration: "5775 días",
      purpose: "Identifica al usuario en el sistema.",
      source: "https://mykredit.es/",
      processor: "mykredit.es"
    },
    {
      name: "app:cookiePolicies",
      provider: "mykredit.es",
      expiration: "5775 días",
      purpose: "Almacena el estado de consentimiento de cookies del usuario para el dominio actual.",
      source: "https://mykredit.es/",
      processor: "mykredit.es"
    }
  ],
  functional: [
    {
      name: "_gid",
      provider: "mykredit.es",
      expiration: "1 día",
      purpose: "Registra una identificación única que se utiliza para generar datos estadísticos acerca de cómo utiliza el visitante el sitio web.",
      source: "https://policies.google.com/privacy",
      processor: "Google"
    },
    {
      name: "1P_JAR",
      provider: "mykredit.es",
      expiration: "1 mes",
      purpose: "Se utiliza para recopilar estadísticas del sitio y realizar un seguimiento de las tasas de conversión.",
      source: "https://policies.google.com/privacy",
      processor: "Google"
    },
    {
      name: "NID/AEC",
      provider: "mykredit.es",
      expiration: "6 meses",
      purpose: "Se utiliza para recordar sus preferencias y otra información, como su idioma preferido.",
      source: "https://policies.google.com/privacy",
      processor: "Google"
    },
    {
      name: "maxtf",
      provider: "mykredit.es",
      expiration: "1 día",
      purpose: "Recopila información sobre el comportamiento del consumidor en el sitio web y guarda información estadística en la plataforma Maxtraffic",
      source: "https://mykredit.es/",
      processor: "mykredit.es"
    },
    {
      name: "maxtp",
      provider: "mykredit.es",
      expiration: "29 días",
      purpose: "Transmite las páginas visitadas por el visitante a la plataforma Maxtraffic",
      source: "https://mykredit.es/",
      processor: "mykredit.es"
    },
    {
      name: "maxtu",
      provider: "mykredit.es",
      expiration: "1 año",
      purpose: "Identifica a una persona en la plataforma Maxtraffic",
      source: "https://mykredit.es/",
      processor: "mykredit.es"
    },
    {
      name: "maxtv",
      provider: "mykredit.es",
      expiration: "1 día",
      purpose: "Pasa el tiempo de visita del visitante a la plataforma Maxtraffic",
      source: "https://mykredit.es/",
      processor: "mykredit.es"
    },
    {
      name: "myk:store_persist:form",
      provider: "mykredit.es",
      expiration: "Persistent",
      purpose: "Utilizado por myKredit.es para hacer una experiencia de envío de formularios óptima y sin problemas para el visitante",
      source: "https://mykredit.es/",
      processor: "mykredit.es"
    }
  ],
  analytics: [
    {
      name: "_ga",
      provider: "mykredit.es",
      expiration: "2 años",
      purpose: "Registra una identificación única que se utiliza para generar datos estadísticos acerca de cómo utiliza el visitante el sitio web.",
      source: "https://policies.google.com/privacy",
      processor: "Google"
    },
    {
      name: "_gat",
      provider: "mykredit.es",
      expiration: "2 años",
      purpose: "Registra una identificación única que se utiliza para generar datos estadísticos acerca de cómo utiliza el visitante el sitio web.",
      source: "https://policies.google.com/privacy",
      processor: "Google"
    },
    {
      name: "_gd#",
      provider: "mykredit.es",
      expiration: "Session",
      purpose: "Utilizado para analizar el uso y el rendimiento de nuestro sitio web y servicios",
      source: "https://policies.google.com/privacy",
      processor: "Google"
    },
    {
      name: "collect",
      provider: "mykredit.es",
      expiration: "Session",
      purpose: "Se utiliza para enviar datos a Google Analytics sobre el dispositivo del visitante y su comportamiento. Rastrea al visitante a través de dispositivos y canales de marketing.",
      source: "https://policies.google.com/privacy",
      processor: "Google"
    },
    {
      name: "OTZ",
      provider: "mykredit.es",
      expiration: "1 mes",
      purpose: "Utilizado por Google Analytics para rastrear la información de tráfico del sitio web",
      source: "https://policies.google.com/privacy",
      processor: "Google"
    },
    {
      name: "DV",
      provider: "mykredit.es",
      expiration: "1 día",
      purpose: "Utilizado por Google Analytics para rastrear la información de tráfico del sitio web",
      source: "https://policies.google.com/privacy",
      processor: "Google"
    }
  ],
  marketing: [
    {
      name: "_fbp",
      provider: "mykredit.es",
      expiration: "3 meses",
      purpose: "Utilizada por Facebook para proporcionar una serie de productos publicitarios como pujas en tiempo real de terceros anunciantes.",
      source: "https://www.facebook.com/policies/cookies/",
      processor: "Facebook"
    },
    {
      name: "_gcl_au",
      provider: "mykredit.es",
      expiration: "3 meses",
      purpose: "Utilizada por Google AdWords para reconectar con visitantes que tienen posibilidades de convertirse en clientes, se basa en el comportamiento online del cliente a través de las webs.",
      source: "https://policies.google.com/privacy",
      processor: "Google"
    },
    {
      name: "fr",
      provider: "mykredit.es",
      expiration: "3 meses",
      purpose: "Utilizada por Facebook para proporcionar una serie de productos publicitarios como pujas en tiempo real de terceros anunciantes.",
      source: "https://www.facebook.com/policies/cookies/",
      processor: "Facebook"
    },
    {
      name: "IDE",
      provider: "mykredit.es",
      expiration: "1 año",
      purpose: "Utilizada por Google DoubleClick para registrar e informar sobre las acciones del usuario en el sitio web tras visualizar o hacer clic en uno de los anuncios del anunciante con el propósito de medir la eficacia de un anuncio y presentar anuncios específicos para el usuario.",
      source: "https://policies.google.com/privacy",
      processor: "Google"
    },
    {
      name: "mt_tab_#",
      provider: "mykredit.es",
      expiration: "Persistent",
      purpose: "No clasificado",
      source: "https://policies.google.com/privacy",
      processor: "Google"
    },
    {
      name: "pagead/1p-user-list/#",
      provider: "mykredit.es",
      expiration: "Session",
      purpose: "Utilizada para rastrear si el visitante ha mostrado un interés específico em productos o eventos a través de múltiples webs y detectar como el visitante navega entre webs - Esto se utiliza para la medida de los esfuerzos publicitarios y facilitar la tasa de emisión entre sitios.",
      source: "https://policies.google.com/privacy",
      processor: "Google"
    },
    {
      name: "pagead/landing",
      provider: "mykredit.es",
      expiration: "Session",
      purpose: "Recoge información del comportamiento del usuario en diferentes webs para mostrar publicidad más relevante - También le permite a la web limitar el número de veces que el usuario está expuesto a un mismo anuncio.",
      source: "https://policies.google.com/privacy",
      processor: "Google"
    },
    {
      name: "tr",
      provider: "mykredit.es",
      expiration: "Session",
      purpose: "Utilizada por Facebook para proporcionar una serie de productos publicitarios como pujas en tiempo real de terceros anunciantes.",
      source: "https://www.facebook.com/policies/cookies/",
      processor: "Facebook"
    },
    {
      name: "dpr",
      provider: "mykredit.es",
      expiration: "1 semana",
      purpose: "Utilizado por Facebook para brindar la mejor experiencia con los anuncios de Facebook para su navegador y dispositivo.",
      source: "https://www.facebook.com/policies/cookies/",
      processor: "Facebook"
    },
    {
      name: "wd",
      provider: "mykredit.es",
      expiration: "1 semana",
      purpose: "Se utiliza para brindar una experiencia óptima para la pantalla de su dispositivo para Meta/Facebook Productos.",
      source: "https://www.facebook.com/policies/cookies/",
      processor: "Facebook"
    },
    {
      name: "datr",
      provider: "mykredit.es",
      expiration: "2 años",
      purpose: "Se utiliza para prevenir el fraude en los productos de Meta/Facebook.",
      source: "https://www.facebook.com/policies/cookies/",
      processor: "Facebook"
    },
    {
      name: "usida",
      provider: "mykredit.es",
      expiration: "Session",
      purpose: "Se utiliza para recopilar una combinación del navegador del visitante y un identificador único, que se utiliza para adaptar la publicidad al visitante.",
      source: "https://www.facebook.com/policies/cookies/",
      processor: "Facebook"
    }
  ],
  fullStory: [
    {
      name: "fs_uid",
      provider: "mykredit.es",
      expiration: "1 año",
      purpose: "Se utiliza para analizar la interacción del visitante con el sitio web para optimizar la experiencia del usuario.",
      source: "https://www.fullstory.com/legal/privacy-policy/",
      processor: "Fullstory"
    }
  ]
};

interface CookiePermissions {
  strictlyNecessary: boolean;
  analyticsPerformance: boolean;
  functional: boolean;
  fullStoryVideo: boolean;
  advertisingPurposing: boolean;
}

interface CookieModalProps {
  approveCookiePermissions: (cookies: CookiePermissions) => void;
}

function CookieModal({ approveCookiePermissions }: CookieModalProps) {

  const modal = useModal();

  const [ isDetailed, setIsDetailed ] = useState<boolean>(false);
  const [ showCookiePolicy, setShowCookiePolicy ] = useState<boolean>(false);
  const [ activeIndex, setActiveIndex ] = useState<number | null>(null);
  const [ cookiePermissions, setCookiePermissions ] = useState<CookiePermissions>({
    strictlyNecessary: true,
    analyticsPerformance: false,
    functional: false,
    fullStoryVideo: false,
    advertisingPurposing: false
  });

  const isOpen = useSelector((state: RootState) => !!state.app.modalState[CookieModal.MODAL]);
  const cookiePolicyTemplate = useCommunicationTemplate("NCP_MyKredit_MX_PoliticaCookies", isOpen);
  const cookieTypesTemplate = useCommunicationTemplate("NCP_MyKredit_MX_TiposCookies", showCookiePolicy);

  const handleCloseModal = () => modal.close(CookieModal.MODAL);

  const selectAllCookies = () => {
    setCookiePermissions((prev) => ({
      ...prev,
      analyticsPerformance: true,
      functional: true,
      fullStoryVideo: true,
      advertisingPurposing: true
    }));
  };

  const acceptAllCookies = () => {
    const cookies = {
      strictlyNecessary: true,
      analyticsPerformance: true,
      functional: true,
      fullStoryVideo: true,
      advertisingPurposing: true
    };
    approveCookiePermissions(cookies);
    handleCloseModal();
  };

  const applyCookies = () => {
    approveCookiePermissions(cookiePermissions);
    handleCloseModal();
  };

  const toggleDetailed = () => setIsDetailed(!isDetailed);

  const toggleShowCookiePolicy = () => {
    setShowCookiePolicy(!showCookiePolicy);
  };

  const handleAccordionTitleClick = (e: React.MouseEvent<HTMLDivElement>, titleProps: AccordionTitleProps) => {
    const { index } = titleProps;
    const numericIndex = typeof index === "number" ? index : null;
    const newIndex = activeIndex === numericIndex ? -1 : numericIndex;
    setActiveIndex(newIndex);
  };

  const handleCheckboxChange = (cookieType: keyof typeof cookiePermissions) => (event: FormEvent<HTMLInputElement>, data: CheckboxProps) => {
    event.stopPropagation();
    setCookiePermissions(prev => ({
      ...prev,
      [cookieType]: !prev[cookieType]
    }));
  };

  return (
    <ModalProvider
      id={CookieModal.MODAL}
      closeOnDimmerClick={false}
      closeOnEscape={false}
      closeOnDocumentClick={false}
      size="small">
      <Modal.Content scrolling>
        {
          !showCookiePolicy && (
            <>
              <strong css={styles.modalTitle}>Política de cookies</strong>
              {cookiePolicyTemplate.isLoading && (
                <Placeholder fluid css={styles.desc}>
                  <PlaceholderParagraph>
                    <PlaceholderLine />
                    <PlaceholderLine />
                  </PlaceholderParagraph>
                </Placeholder>
              )}
              {!cookiePolicyTemplate.isLoading && !cookiePolicyTemplate.content && (
                <p css={styles.desc}>
                  Utilizamos propias cookies y de terceros para analizar y mejorar su navegación. Al hacer clic en 'Aceptar', aceptara el usa de cookies.
                  <span css={styles.cookiePolicyLink} onClick={toggleShowCookiePolicy}> Acerca de Cookies </span>
                </p>
              )}
              {!!cookiePolicyTemplate.content && (
                <div css={styles.desc}>
                  <div dangerouslySetInnerHTML={{ __html: cookiePolicyTemplate.content }} />
                  <span css={styles.cookiePolicyLink} onClick={toggleShowCookiePolicy}>Acerca de Cookies</span>
                </div>
              )}
            </>
          )
        }
        {isDetailed && !showCookiePolicy && (
          <div>
            <Button
              css={styles.selectAllBtn}
              size="mini"
              onClick={selectAllCookies}>
              SELECCIONAR TODO
            </Button>
            <>
            </>
            <Accordion>
              <Accordion.Title
                active={activeIndex === 0}
                index={0}
                onClick={handleAccordionTitleClick}
                css={styles.titleContainer}>
                <Icon name="dropdown" />
                <strong css={styles.titleContent}>
                  Estrictamente necesarias
                </strong>
                <div css={styles.checkbox}>
                  <Form.Checkbox
                    toggle
                    checked
                    disabled />
                </div>
              </Accordion.Title>
              <div css={styles.detail}>
                Estas cookies permiten servicios que los usuarios han solicitado específicamente. Las cookies estrictamente necesarias son esenciales para el funcionamiento de un sitio web y permiten a los usuarios moverse por sitios web y utilizar sus funciones, p.e. Cookies que permiten a los usuarios iniciar sesión en áreas seguras de un sitio web.
              </div>
              <Accordion.Content
                active={activeIndex === 0}
                css={styles.detail}>
                <hr />
                {COOKIE_ITEMS.necessary.map(item => {
                  return <CookieDetail key={item.name} toggleShowCookiePolicy={toggleShowCookiePolicy} cookie={item} />;
                })}
              </Accordion.Content>
              <Accordion.Title
                active={activeIndex === 1}
                index={1}
                onClick={handleAccordionTitleClick}
                css={styles.titleContainer}>
                <Icon name="dropdown" />
                <strong css={styles.titleContent}>
                  Funcionales
                </strong>
                <div css={styles.checkbox}>
                  <Form.Checkbox
                    toggle
                    onChange={handleCheckboxChange("functional")}
                    checked={cookiePermissions.functional} />
                </div>
              </Accordion.Title>
              <div css={styles.detail}>
                Estas cookies permiten a los sitios web recordar las opciones que los usuarios hacen (por ejemplo, nombre de usuario o idioma) y permitir a los propietarios de sitios web personalizar su contenido para los usuarios, darles la bienvenida y recordar las preferencias de los usuarios para mejorar su experiencia. Pueden usarse para proporcionar servicios que los usuarios han solicitado, p.e.
              </div>
              <Accordion.Content
                active={activeIndex === 1}
                css={styles.detail}>
                <hr />
                {COOKIE_ITEMS.functional.map(item => {
                  return <CookieDetail key={item.name} toggleShowCookiePolicy={toggleShowCookiePolicy} cookie={item} />;
                })}
              </Accordion.Content>
              <Accordion.Title
                active={activeIndex === 2}
                index={2}
                onClick={handleAccordionTitleClick}
                css={styles.titleContainer}>
                <Icon name="dropdown" />
                <strong css={styles.titleContent}>
                  Analíticas / de rendimiento
                </strong>
                <div css={styles.checkbox}>
                  <Form.Checkbox
                    toggle
                    onChange={handleCheckboxChange("analyticsPerformance")}
                    checked={cookiePermissions.analyticsPerformance} />
                </div>
              </Accordion.Title>
              <div css={styles.detail}>
                Estas cookies recopilan información sobre cómo los visitantes usan los sitios web. Dicha información es agregada de forma anónima y no es personalmente identificable y se utiliza para mejorar el funcionamiento de los sitios web, p.e. Estas cookies pueden reconocer y contar el número de visitantes del sitio web y ver cómo los visitantes se mueven en sitios web.
              </div>
              <Accordion.Content
                active={activeIndex === 2}
                css={styles.detail}>
                <hr />
                {COOKIE_ITEMS.analytics.map(item => {
                  return <CookieDetail key={item.name} toggleShowCookiePolicy={toggleShowCookiePolicy} cookie={item} />;
                })}
              </Accordion.Content>
              <Accordion.Title
                active={activeIndex === 3}
                index={3}
                onClick={handleAccordionTitleClick}
                css={styles.titleContainer}>
                <Icon name="dropdown" />
                <strong css={styles.titleContent}>
                  Viendo un video
                </strong>
                <div css={styles.checkbox}>
                  <Form.Checkbox
                    toggle
                    onChange={handleCheckboxChange("fullStoryVideo")}
                    checked={cookiePermissions.fullStoryVideo} />
                </div>
              </Accordion.Title>
              <div css={styles.detail}>
                Las cookies de marketing se utilizan para rastrear a los visitantes en las páginas web. La intención es mostrar anuncios relevantes y atractivos para el usuario individual, y por lo tanto, más valiosos para los editores y terceros anunciantes.
              </div>
              <Accordion.Content
                active={activeIndex === 3}
                css={styles.detail}>
                <hr />
                {COOKIE_ITEMS.fullStory.map(item => {
                  return <CookieDetail key={item.name} toggleShowCookiePolicy={toggleShowCookiePolicy} cookie={item} />;
                })}
              </Accordion.Content>
              <Accordion.Title
                active={activeIndex === 4}
                index={4}
                onClick={handleAccordionTitleClick}
                css={styles.titleContainer}>
                <Icon name="dropdown" />
                <strong css={styles.titleContent}>
                  Publicidad
                </strong>
                <div css={styles.checkbox}>
                  <Form.Checkbox
                    toggle
                    onChange={handleCheckboxChange("advertisingPurposing")}
                    checked={cookiePermissions.advertisingPurposing} />
                </div>
              </Accordion.Title>
              <div css={styles.detail}>
                A menudo, las cookies de orientación / publicidad están vinculadas a la funcionalidad del sitio proporcionada por terceros.
              </div>
              <Accordion.Content
                active={activeIndex === 4}
                css={styles.detail}>
                <hr />
                {COOKIE_ITEMS.marketing.map(item => {
                  return <CookieDetail key={item.name} toggleShowCookiePolicy={toggleShowCookiePolicy} cookie={item} />;
                })}
              </Accordion.Content>
            </Accordion>
          </div>
        )}
        {
          showCookiePolicy && (
            <div css={styles.cookiePolicy}>
              {cookieTypesTemplate.isLoading && (
                <Placeholder fluid>
                  <PlaceholderHeader>
                    <PlaceholderLine />
                  </PlaceholderHeader>
                  <PlaceholderParagraph>
                    <PlaceholderLine />
                    <PlaceholderLine />
                    <PlaceholderLine />
                    <PlaceholderLine />
                  </PlaceholderParagraph>
                </Placeholder>
              )}
              {!cookieTypesTemplate.isLoading && !cookieTypesTemplate.content && (
                <p>No pudimos cargar la información sobre cookies. Inténtalo de nuevo más tarde.</p>
              )}
              {!!cookieTypesTemplate.content && (
                <div dangerouslySetInnerHTML={{ __html: cookieTypesTemplate.content }} />
              )}
            </div>
          )
        }
        <div css={styles.buttons}>
          <Button
            id="cookie-accept-btn"
            fluid
            size="small"
            theme="orange"
            onClick={isDetailed && !showCookiePolicy ? applyCookies : acceptAllCookies}>
            {isDetailed && !showCookiePolicy ? "ACTUALIZAR CONSENTIMIENTO" : "ACEPTAR"}
          </Button>
          <Button
            unstyled
            contentStyles={styles.detailBtn}
            fluid
            onClick={showCookiePolicy ? toggleShowCookiePolicy : toggleDetailed}
            style={{
              fontSize: 14,
              color: COLORS.primary,
              width: "100%",
              marginTop: 8,
              ":hover": {
                color: COLORS.primary
              }
            }}
            content={showCookiePolicy ? "Controles de Privacidad" : isDetailed ? "Ocultar detalles" : "Mostrar detalles"} />

        </div>
      </Modal.Content>
    </ModalProvider>
  );
}

CookieModal.MODAL = "cookieModal";

export default CookieModal;
